// WEST DEKLE live chat bubble (sections/wd-chat.liquid).
// Talks to the Telegram relay (relay/worker.js): POST /chat/send, GET /chat/poll.
// Visitors enter their name and email before the first message (kept in this browser for next time).
// Any element with [data-wd-chat-open] is revealed and opens the chat when clicked.
(() => {
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch {} },
  };
  const newSid = () => {
    const a = new Uint8Array(20);
    crypto.getRandomValues(a);
    return [...a].map((b) => (b % 36).toString(36)).join('');
  };

  class WdChat extends HTMLElement {
    connectedCallback() {
      this.endpoint = (this.dataset.endpoint || '').replace(/\/+$/, '');
      this.sid = store.get('wd-chat-sid') || '';
      this.last = 0;
      this.seen = Number(store.get('wd-chat-seen')) || 0;
      this.messages = [];
      try { this.profile = JSON.parse(store.get('wd-chat-profile') || 'null'); } catch { this.profile = null; }

      this.toggle = this.querySelector('.wdchat__toggle');
      this.panel = this.querySelector('.wdchat__panel');
      this.list = this.querySelector('.wdchat__messages');
      this.form = this.querySelector('.wdchat__form');
      this.input = this.querySelector('.wdchat__input');
      this.startForm = this.querySelector('.wdchat__start');
      this.status = this.querySelector('.wdchat__status');
      this.badge = this.querySelector('.wdchat__badge');

      this.toggle.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      this.querySelector('.wdchat__close').addEventListener('click', () => this.close());
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.send(); });
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(); }
      });
      this.startForm.addEventListener('submit', (e) => { e.preventDefault(); this.start(); });
      this.showForms();
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.panel.hidden) this.close(); });

      document.querySelectorAll('[data-wd-chat-open]').forEach((el) => {
        el.hidden = false;
        el.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });
      document.addEventListener('visibilitychange', () => this.schedule());

      // Returning visitor with a conversation: fetch it so replies show as a badge.
      if (this.sid) this.poll();
    }

    open() {
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      this.markSeen();
      (this.profile ? this.input : this.startForm.elements.name).focus();
      this.scrollDown();
      this.schedule();
    }

    close() {
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.toggle.focus();
      this.schedule();
    }

    // Poll fast while open, slowly while closed with a live conversation, never when hidden or idle for 30 min.
    schedule() {
      clearTimeout(this.timer);
      if (!this.sid || document.visibilityState === 'hidden') return;
      const idle = Date.now() - (this.lastActivity || 0);
      if (idle > 30 * 60e3) return;
      const delay = this.panel.hidden ? 20e3 : idle < 3 * 60e3 ? 3e3 : 10e3;
      this.timer = setTimeout(() => this.poll(), delay);
    }

    async poll() {
      try {
        const r = await fetch(`${this.endpoint}/chat/poll?sid=${this.sid}&after=${this.last}`, { cache: 'no-store' });
        if (r.ok) {
          const { messages } = await r.json();
          messages.forEach((m) => this.addMessage(m));
          if (!this.lastActivity && messages.length) this.lastActivity = messages.at(-1).ts;
        }
      } catch {}
      this.schedule();
    }

    addMessage(m) {
      if (m.id && m.id <= this.last) return;
      if (m.id) this.last = m.id;
      if (m.from === 'agent') this.lastActivity = Date.now();
      // Replace the optimistic copy of a visitor message once the server confirms it.
      const pending = m.from === 'visitor' && this.list.querySelector('.wdchat__msg--pending');
      if (pending) { pending.classList.remove('wdchat__msg--pending'); this.messages.push(m); return this.updateBadge(); }
      this.messages.push(m);
      this.render(m);
      this.updateBadge();
    }

    render(m, pending) {
      const el = document.createElement('div');
      el.className = `wdchat__msg wdchat__msg--${m.from}${pending ? ' wdchat__msg--pending' : ''}`;
      el.textContent = m.text;
      if (m.from === 'agent') {
        const who = document.createElement('span');
        who.className = 'wdchat__who';
        who.textContent = this.dataset.agentName || 'West Dekle team';
        el.prepend(who);
      }
      this.list.append(el);
      this.scrollDown();
    }

    scrollDown() { this.list.scrollTop = this.list.scrollHeight; }

    updateBadge() {
      if (!this.panel.hidden) return this.markSeen();
      const unread = this.messages.filter((m) => m.from === 'agent' && m.id > this.seen).length;
      this.badge.hidden = !unread;
      this.badge.textContent = unread;
    }

    markSeen() {
      this.seen = this.last;
      store.set('wd-chat-seen', String(this.seen));
      this.badge.hidden = true;
    }

    say(text) { this.status.textContent = text; this.status.hidden = !text; }

    async send() {
      const text = this.input.value.trim();
      if (!text || this.sending) return;
      if (!this.sid) { this.sid = newSid(); store.set('wd-chat-sid', this.sid); }
      this.sending = true;
      this.say('');
      this.render({ from: 'visitor', text }, true);
      this.input.value = '';
      try {
        const r = await fetch(`${this.endpoint}/chat/send`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ sid: this.sid, name: this.profile?.name, email: this.profile?.email, text, page: location.href.split('?')[0] }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'send');
        this.lastActivity = Date.now();
        this.poll();
      } catch (err) {
        this.list.querySelector('.wdchat__msg--pending')?.remove();
        this.input.value = text;
        if (err.message === 'profile') { this.profile = null; this.showForms(); }
        const errors = {
          profile: 'Please enter your name and email to start the chat.',
          rate: 'You are sending messages quickly. Please wait a moment.',
          offline: this.dataset.offline,
        };
        this.say(errors[err.message] || 'Message not sent. Check your connection and try again.');
      } finally {
        this.sending = false;
      }
    }

    // Before name and email are given, the panel shows only the start form; the greeting and messages come after.
    showForms() {
      this.startForm.hidden = !!this.profile;
      this.form.hidden = !this.profile;
      this.list.hidden = !this.profile;
      this.classList.toggle('has-profile', !!this.profile);
    }

    start() {
      const f = this.startForm.elements;
      if (!this.startForm.reportValidity()) return;
      this.profile = { name: f.name.value.trim(), email: f.email.value.trim() };
      store.set('wd-chat-profile', JSON.stringify(this.profile));
      this.showForms();
      this.render({ from: 'system', text: `Thanks, ${this.profile.name}! ${this.dataset.notice || ''}`.trim() });
      this.scrollDown();
      this.input.focus();
    }
  }

  if (!customElements.get('wd-chat')) customElements.define('wd-chat', WdChat);
})();
